import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot, 
  Timestamp, 
  DocumentData,
  QuerySnapshot,
  Unsubscribe,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';

// GL Account interface
export interface GLAccount {
  id?: string;
  accountNumber: string;
  accountName: string;
  accountType: string;
  parentAccount?: string;
  level: number; // 1, 2, or 3 for the hierarchy level
  description?: string;
  isActive: boolean;
  createdAt?: Timestamp | Date;
  updatedAt?: Timestamp | Date;
  balanceType: 'debit' | 'credit'; // Whether account normally has debit or credit balance
  currentBalance?: number;
}

// Standard account types
export const accountTypes = [
  'Assets',
  'Liabilities',
  'Equity',
  'Revenue',
  'Expenses'
];

// Standard account type number ranges with first digit indicating type
export const accountTypeRanges = {
  'Assets': { start: 1000000, end: 1999999, prefix: '1' },
  'Liabilities': { start: 2000000, end: 2999999, prefix: '2' },
  'Equity': { start: 3000000, end: 3999999, prefix: '3' },
  'Revenue': { start: 4000000, end: 4999999, prefix: '4' },
  'Expenses': { start: 5000000, end: 5999999, prefix: '5' }
};

// Get all GL accounts
export const getGLAccounts = async (): Promise<GLAccount[]> => {
  try {
    const accountsQuery = query(
      collection(db, 'glAccounts'),
      orderBy('accountNumber', 'asc')
    );
    
    const snapshot = await getDocs(accountsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as GLAccount));
  } catch (error) {
    console.error('Error getting GL accounts:', error);
    throw error;
  }
};

// Subscribe to GL accounts (real-time updates)
export const subscribeToGLAccounts = (
  callback: (accounts: GLAccount[]) => void,
  level?: number
): Unsubscribe => {
  try {
    let accountsQuery;
    
    if (level !== undefined) {
      accountsQuery = query(
        collection(db, 'glAccounts'),
        where('level', '==', level),
        orderBy('accountNumber', 'asc')
      );
    } else {
      accountsQuery = query(
        collection(db, 'glAccounts'),
        orderBy('accountNumber', 'asc')
      );
    }
    
    return onSnapshot(accountsQuery, (snapshot: QuerySnapshot<DocumentData>) => {
      const accounts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as GLAccount));
      
      callback(accounts);
    });
  } catch (error) {
    console.error('Error subscribing to GL accounts:', error);
    throw error;
  }
};

// Get GL account by ID
export const getGLAccountById = async (accountId: string): Promise<GLAccount | null> => {
  try {
    const accountDoc = await getDoc(doc(db, 'glAccounts', accountId));
    
    if (accountDoc.exists()) {
      return {
        id: accountDoc.id,
        ...accountDoc.data()
      } as GLAccount;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting GL account:', error);
    throw error;
  }
};

// Get GL account by account number
export const getGLAccountByNumber = async (accountNumber: string): Promise<GLAccount | null> => {
  try {
    const accountsQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '==', accountNumber),
      limit(1)
    );
    
    const snapshot = await getDocs(accountsQuery);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as GLAccount;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting GL account by number:', error);
    throw error;
  }
};

// Get GL accounts by parent account
export const getGLAccountsByParent = async (parentAccountNumber: string): Promise<GLAccount[]> => {
  try {
    const accountsQuery = query(
      collection(db, 'glAccounts'),
      where('parentAccount', '==', parentAccountNumber),
      orderBy('accountNumber', 'asc')
    );
    
    const snapshot = await getDocs(accountsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as GLAccount));
  } catch (error) {
    console.error('Error getting GL accounts by parent:', error);
    throw error;
  }
};

// Create a clearing account for a main account
export const createClearingAccount = async (mainAccount: GLAccount): Promise<string> => {
  try {
    // Extract the first digit from the main account number (e.g., 1 from 1000000)
    const accountPrefix = mainAccount.accountNumber.charAt(0);
    // Use a special format for clearing accounts that won't conflict with regular accounts
    // Format: [first digit]9[first digit]9999 (e.g., 1919999 for account type 1)
    const clearingAccountNumber = `${accountPrefix}9${accountPrefix}9999`;
    
    // Check if clearing account already exists
    const clearingAccountQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '==', clearingAccountNumber)
    );
    
    const clearingSnapshot = await getDocs(clearingAccountQuery);
    
    if (!clearingSnapshot.empty) {
      // Clearing account already exists, return its ID
      return clearingSnapshot.docs[0].id;
    }
    
    // Create new clearing account
    const now = new Date();
    const clearingAccountData: GLAccount = {
      accountNumber: clearingAccountNumber,
      accountName: `Clearing Account for ${mainAccount.accountName}`,
      accountType: mainAccount.accountType,
      parentAccount: mainAccount.accountNumber, // Use account number instead of ID
      level: 2, // Sub-account level
      description: `Clearing account for ${mainAccount.accountType} transactions`,
      isActive: true,
      balanceType: mainAccount.balanceType,
      currentBalance: 0,
      createdAt: now,
      updatedAt: now
    };
    
    const clearingAccountRef = doc(collection(db, 'glAccounts'));
    await setDoc(clearingAccountRef, clearingAccountData);
    
    return clearingAccountRef.id;
  } catch (error) {
    console.error('Error creating clearing account:', error);
    throw error;
  }
};

// Create or update GL account
export const saveGLAccount = async (account: GLAccount): Promise<string> => {
  try {
    const now = new Date();
    
    if (account.id) {
      // Update existing account
      const accountRef = doc(db, 'glAccounts', account.id);
      await updateDoc(accountRef, {
        ...account,
        updatedAt: now
      });
      return account.id;
    } else {
      // Create new account
      // Remove the id field if it's undefined to prevent Firestore errors
      const { id, ...accountWithoutId } = account;
      
      const accountData = {
        ...accountWithoutId,
        createdAt: now,
        updatedAt: now
      };
      
      const newAccountRef = doc(collection(db, 'glAccounts'));
      await setDoc(newAccountRef, accountData);
      
      // If this is a main account (level 1), create a clearing account for it
      if (account.level === 1) {
        try {
          // We need to pass the account with its new ID
          await createClearingAccount({
            ...accountData,
            id: newAccountRef.id
          } as GLAccount);
        } catch (clearingError) {
          console.error('Error creating clearing account:', clearingError);
          // Continue even if clearing account creation fails
          // The main account is still created
        }
      }
      
      return newAccountRef.id;
    }
  } catch (error) {
    console.error('Error saving GL account:', error);
    throw error;
  }
};

// Delete GL account
export const deleteGLAccount = async (accountId: string): Promise<void> => {
  try {
    // Check if account has children
    const childrenQuery = query(
      collection(db, 'glAccounts'),
      where('parentAccount', '==', accountId)
    );
    
    const childrenSnapshot = await getDocs(childrenQuery);
    
    if (!childrenSnapshot.empty) {
      throw new Error('Cannot delete account with child accounts. Delete children first.');
    }
    
    // Check if account is used in transactions (implementation would depend on your transaction structure)
    // This is a placeholder for that check
    
    // Delete the account
    await deleteDoc(doc(db, 'glAccounts', accountId));
  } catch (error) {
    console.error('Error deleting GL account:', error);
    throw error;
  }
};

// Fix parentAccount references in clearing accounts
export const fixClearingAccountReferences = async (): Promise<void> => {
  try {
    // Get all clearing accounts (those with account numbers ending in 999999)
    const clearingAccountsQuery = query(
      collection(db, 'glAccounts'),
      where('level', '==', 2),
      orderBy('accountNumber', 'asc')
    );
    
    const clearingSnapshot = await getDocs(clearingAccountsQuery);
    
    if (clearingSnapshot.empty) {
      console.log('No clearing accounts found to fix.');
      return;
    }
    
    // Get all main accounts for reference
    const mainAccountsQuery = query(
      collection(db, 'glAccounts'),
      where('level', '==', 1),
      orderBy('accountNumber', 'asc')
    );
    
    const mainAccountsSnapshot = await getDocs(mainAccountsQuery);
    const mainAccounts = mainAccountsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as GLAccount));
    
    // Fix each clearing account
    const batch = writeBatch(db);
    let fixCount = 0;
    
    for (const doc of clearingSnapshot.docs) {
      const clearingAccount = {
        id: doc.id,
        ...doc.data()
      } as GLAccount;
      
      // Check if the account number ends with 999999
      if (clearingAccount.accountNumber.endsWith('999999')) {
        const accountPrefix = clearingAccount.accountNumber.charAt(0);
        
        // Find the corresponding main account
        const mainAccount = mainAccounts.find(account => 
          account.accountNumber.startsWith(accountPrefix) && 
          account.level === 1
        );
        
        if (mainAccount && clearingAccount.parentAccount !== mainAccount.accountNumber) {
          // Update the parentAccount reference
          batch.update(doc.ref, {
            parentAccount: mainAccount.accountNumber,
            updatedAt: new Date()
          });
          
          fixCount++;
        }
      }
    }
    
    if (fixCount > 0) {
      await batch.commit();
      console.log(`Fixed ${fixCount} clearing account references`);
    } else {
      console.log('No clearing account references needed fixing');
    }
  } catch (error) {
    console.error('Error fixing clearing account references:', error);
    throw error;
  }
};

// Create clearing accounts for all existing main accounts
export const createClearingAccountsForExistingMainAccounts = async (): Promise<void> => {
  try {
    // Get all main accounts (level 1)
    const mainAccountsQuery = query(
      collection(db, 'glAccounts'),
      where('level', '==', 1),
      orderBy('accountNumber', 'asc')
    );
    
    const mainAccountsSnapshot = await getDocs(mainAccountsQuery);
    
    if (mainAccountsSnapshot.empty) {
      console.log('No main accounts found to create clearing accounts for.');
      return;
    }
    
    // Create clearing accounts for each main account
    const promises = mainAccountsSnapshot.docs.map(async (doc) => {
      const mainAccount = {
        id: doc.id,
        ...doc.data()
      } as GLAccount;
      
      try {
        await createClearingAccount(mainAccount);
        console.log(`Created clearing account for ${mainAccount.accountName} (${mainAccount.accountNumber})`);
      } catch (error) {
        console.error(`Error creating clearing account for ${mainAccount.accountName}:`, error);
      }
    });
    
    await Promise.all(promises);
    console.log('Finished creating clearing accounts for all main accounts');
  } catch (error) {
    console.error('Error creating clearing accounts for existing main accounts:', error);
    throw error;
  }
};

// Helper function to create AR sub-account
const createARSubAccount = async (mainAccountId: string): Promise<string> => {
  try {
    // Get the main account
    const mainAccountRef = doc(db, 'glAccounts', mainAccountId);
    const mainAccountDoc = await getDoc(mainAccountRef);
    
    if (!mainAccountDoc.exists()) {
      throw new Error('Main account not found');
    }
    
    const mainAccount = { id: mainAccountId, ...mainAccountDoc.data() } as GLAccount;
    
    // Create the sub-account
    const now = new Date();
    const subAccountData: GLAccount = {
      accountNumber: '1200001',
      accountName: 'General-Accounts Receivable',
      accountType: 'Assets',
      parentAccount: mainAccount.accountNumber,
      level: 3, // Detail account level
      description: 'General accounts receivable for customer invoices',
      isActive: true,
      balanceType: 'debit', // Assets are debit-normal
      currentBalance: 0,
      createdAt: now,
      updatedAt: now
    };
    
    const subAccountRef = doc(collection(db, 'glAccounts'));
    await setDoc(subAccountRef, subAccountData);
    
    console.log('Created General-Accounts Receivable sub-account');
    return subAccountRef.id;
  } catch (error) {
    console.error('Error creating AR sub-account:', error);
    throw error;
  }
};

// Create a default accounts receivable account if none exists
export const createDefaultAccountsReceivable = async (): Promise<string> => {
  try {
    // Check if accounts receivable account already exists
    // Query for accounts with account numbers starting with '12' (AR accounts)
    const arQuery = query(
      collection(db, 'glAccounts'),
      where('accountNumber', '>=', '1200000'),
      where('accountNumber', '<', '1300000'),
      limit(1)
    );
    
    const arSnapshot = await getDocs(arQuery);
    const arAccounts = arSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as GLAccount));
    
    if (arAccounts.length > 0) {
      // Check if we have a specific AR sub-account (1200001)
      const arSubQuery = query(
        collection(db, 'glAccounts'),
        where('accountNumber', '==', '1200001')
      );
      
      const arSubSnapshot = await getDocs(arSubQuery);
      
      if (!arSubSnapshot.empty) {
        console.log('General Accounts Receivable sub-account already exists');
        return arSubSnapshot.docs[0].id || '';
      }
      
      // If main account exists but sub-account doesn't, create the sub-account
      const mainAccount = arAccounts[0];
      return await createARSubAccount(mainAccount.id || '');
    }
    
    // Create a new accounts receivable main account
    const now = new Date();
    const mainAccountData: GLAccount = {
      accountNumber: '1200000',
      accountName: 'Accounts Receivable',
      accountType: 'Assets',
      level: 1, // Main account level
      description: 'Accounts receivable main account',
      isActive: true,
      balanceType: 'debit', // Assets are debit-normal
      currentBalance: 0,
      createdAt: now,
      updatedAt: now
    };
    
    const mainAccountRef = doc(collection(db, 'glAccounts'));
    await setDoc(mainAccountRef, mainAccountData);
    
    // Create a clearing account for this main account
    await createClearingAccount({
       ...mainAccountData,
      id: mainAccountRef.id
    } as GLAccount);
    
    console.log('Created default accounts receivable main account');
    
    // Now create the sub-account
    return await createARSubAccount(mainAccountRef.id);
  } catch (error) {
    console.error('Error creating default accounts receivable account:', error);
    throw error;
  }
};

// Migrate existing clearing accounts to the new format
export const migrateExistingClearingAccounts = async (): Promise<void> => {
  try {
    // Find all clearing accounts with the old format (ending with 999999)
    const oldClearingAccountsQuery = query(
      collection(db, 'glAccounts'),
      where('level', '==', 2),
      orderBy('accountNumber', 'asc')
    );
    
    const snapshot = await getDocs(oldClearingAccountsQuery);
    
    if (snapshot.empty) {
      console.log('No clearing accounts to migrate');
      return;
    }
    
    const batch = writeBatch(db);
    let batchCount = 0;
    const maxBatchSize = 500; // Firestore batch limit
    let migratedCount = 0;
    
    for (const docSnapshot of snapshot.docs) {
      const account = { id: docSnapshot.id, ...docSnapshot.data() } as GLAccount;
      
      // Check if this is a clearing account (ends with 999999)
      if (account.accountNumber.endsWith('999999')) {
        const accountPrefix = account.accountNumber.charAt(0);
        // New format: [first digit]9[first digit]9999
        const newAccountNumber = `${accountPrefix}9${accountPrefix}9999`;
        
        // Update the account with the new number
        batch.update(doc(db, 'glAccounts', account.id!), {
          accountNumber: newAccountNumber,
          updatedAt: new Date()
        });
        
        batchCount++;
        migratedCount++;
        
        // If we reach the batch limit, commit and start a new batch
        if (batchCount >= maxBatchSize) {
          await batch.commit();
          batchCount = 0;
        }
      }
    }
    
    // Commit any remaining operations
    if (batchCount > 0) {
      await batch.commit();
    }
    
    console.log(`Migrated ${migratedCount} clearing accounts to the new format`);
  } catch (error) {
    console.error('Error migrating clearing accounts:', error);
    throw error;
  }
};

// Generate next available account number based on parent and level
export const generateNextAccountNumber = async (
  parentAccountNumber?: string,
  accountType?: string,
  level?: number
): Promise<string> => {
  try {
    if (level === 1) {
      // For level 1 (main accounts), find the next available main account number
      // Get all level 1 accounts (excluding clearing accounts)
      const mainAccountsQuery = query(
        collection(db, 'glAccounts'),
        where('level', '==', 1),
        orderBy('accountNumber', 'asc')
      );
      
      const snapshot = await getDocs(mainAccountsQuery);
      
      if (snapshot.empty) {
        // No main accounts yet, start with Assets (1000000)
        return '1000000';
      }
      
      // Get all existing main account numbers
      const existingMainAccounts = snapshot.docs.map(doc => {
        const data = doc.data() as GLAccount;
        return data.accountNumber;
      });
      
      // Find the next available main account number (1000000, 2000000, 3000000, etc.)
      for (let i = 1; i <= 9; i++) {
        const potentialNumber = `${i}000000`;
        if (!existingMainAccounts.includes(potentialNumber)) {
          return potentialNumber;
        }
      }
      
      throw new Error('Maximum number of main account types reached');
    } else if (level === 2 && parentAccountNumber) {
      // For level 2 (sub accounts), base on parent account
      // Get all sub accounts under this parent
      const subAccountsQuery = query(
        collection(db, 'glAccounts'),
        where('parentAccount', '==', parentAccountNumber),
        where('level', '==', 2),
        orderBy('accountNumber', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(subAccountsQuery);
      
      // Get all regular sub-accounts (non-clearing) for this parent's first digit
      const parentPrefix = parentAccountNumber.charAt(0);
      
      // Get all sub-accounts that start with the same first digit
      const allSubAccountsQuery = query(
        collection(db, 'glAccounts'),
        where('level', '==', 2),
        where('accountNumber', '>=', `${parentPrefix}000000`),
        where('accountNumber', '<', `${parentPrefix}900000`), // Exclude clearing accounts (9xxxxx)
        orderBy('accountNumber', 'asc')
      );
      
      const allSubAccounts = await getDocs(allSubAccountsQuery);
      
      // Filter out clearing accounts (those with account names containing 'Clearing')
      const regularSubAccounts = allSubAccounts.docs
        .map(doc => ({ id: doc.id, ...doc.data() }) as GLAccount)
        .filter(account => !account.accountName.includes('Clearing'));
      
      // Get all used second digits from regular accounts
      const usedSecondDigits = new Set<number>();
      regularSubAccounts.forEach(account => {
        const secondDigit = parseInt(account.accountNumber.charAt(1));
        if (!isNaN(secondDigit)) {
          usedSecondDigits.add(secondDigit);
        }
      });
      
      // Find the next available second digit (1-8, avoiding 9 which is for clearing accounts)
      let nextDigit = 1;
      while (nextDigit <= 8 && usedSecondDigits.has(nextDigit)) {
        nextDigit++;
      }
      
      if (nextDigit > 8) {
        throw new Error(`Maximum number of sub accounts reached for this parent`);
      }
      
      // Create the new account number with the next available second digit
      return `${parentPrefix}${nextDigit}00000`;
    } else if (level === 3 && parentAccountNumber) {
      // For level 3 (detail accounts), base on parent sub account
      // Get all detail accounts under this parent (excluding clearing accounts)
      const detailAccountsQuery = query(
        collection(db, 'glAccounts'),
        where('parentAccount', '==', parentAccountNumber),
        where('level', '==', 3),
        orderBy('accountNumber', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(detailAccountsQuery);
      
      if (snapshot.empty) {
        // First detail account - use parent number + 1
        // For example, if parent is 1100000, first detail is 1100001
        return (parseInt(parentAccountNumber) + 1).toString();
      } else {
        // Get the highest detail account and add 1
        // This ensures detail accounts are sequential: 1100001, 1100002, 1100003, etc.
        const highestDetailAccount = snapshot.docs[0].data() as GLAccount;
        const highestNumber = parseInt(highestDetailAccount.accountNumber);
        const nextDetailNumber = highestNumber + 1;
        
        // Ensure we don't exceed the sub account range
        const subPrefix = parentAccountNumber.substring(0, 2);
        const maxDetailNumber = parseInt(`${subPrefix}99999`);
        
        if (nextDetailNumber >= maxDetailNumber) {
          throw new Error(`Maximum number of detail accounts reached for this sub account`);
        }
        
        return nextDetailNumber.toString();
      }
    } else {
      throw new Error('Invalid parameters for account number generation');
    }
  } catch (error) {
    console.error('Error generating next account number:', error);
    throw error;
  }
};
